// src/api.js
import axios from "axios";
import config from "./config";
import { store } from "./store/store";
import { login as loginAction, logout as logoutAction } from "./store/authSlice";

const api = axios.create({
  baseURL: config.apiUrl,
  withCredentials: true, // needed for HttpOnly refresh cookie
});

// ------------------------------------------------------------
// CSRF helper (for refresh/logout when using cookies)
// ------------------------------------------------------------
const getCookie = (name) => {
  const match = document.cookie.match(
    new RegExp(
      `(?:^|; )${name.replace(/([.$?*|{}()\\[\]\\/+^])/g, "\\$1")}=([^;]*)`
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
};

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!["get", "head", "options"].includes(method)) {
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      config.headers["X-CSRFToken"] = csrfToken;
    }
  }
  return config;
});

// ------------------------------------------------------------
// Auth header helpers
// ------------------------------------------------------------
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

const authConfig = (token) =>
  token
    ? {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    : undefined;

// ------------------------------------------------------------
// Auth endpoints (Django)
// ------------------------------------------------------------
export const registerUser = (payload) =>
  api.post("/api/auth/register/", payload);

export const loginUser = (username, password) =>
  api.post("/api/auth/login/", { username, password });

export const logoutUser = () => api.post("/api/auth/logout/");

export const refreshAccessToken = () => api.post("/api/auth/refresh/");

// ------------------------------------------------------------
// Response interceptor: refresh access token on 401 and retry once.
// ------------------------------------------------------------
const REFRESH_URL = "/api/auth/refresh/";
const LOGIN_URL = "/api/auth/login/";

let refreshPromise = null;

const performLogout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("username");
  localStorage.removeItem("is_admin");
  localStorage.removeItem("expires_at");
  setAuthToken(null);
  try {
    store.dispatch(logoutAction());
  } catch {
    // store not ready
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config: original } = error;
    if (!response || !original || original._retry) {
      return Promise.reject(error);
    }
    const url = original.url || "";
    // Don't try to refresh for the auth endpoints themselves.
    if (url.includes(REFRESH_URL) || url.includes(LOGIN_URL)) {
      if (response.status === 401 && url.includes(REFRESH_URL)) {
        performLogout();
      }
      return Promise.reject(error);
    }
    if (response.status !== 401) {
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const { data } = await refreshPromise;
      const newToken = data?.access;
      if (!newToken) throw new Error("No access token in refresh response");

      const username =
        store.getState()?.auth?.username || localStorage.getItem("username");
      const isAdmin =
        store.getState()?.auth?.is_admin ||
        localStorage.getItem("is_admin") === "true";
      const expiresAt = Date.now() + 15 * 60 * 1000;

      localStorage.setItem("token", newToken);
      localStorage.setItem("expires_at", String(expiresAt));
      setAuthToken(newToken);
      store.dispatch(
        loginAction({
          token: newToken,
          username,
          is_admin: isAdmin,
          expires_at: expiresAt,
        })
      );

      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newToken}`;
      return api(original);
    } catch (refreshErr) {
      performLogout();
      return Promise.reject(refreshErr);
    }
  }
);

export const fetchMe = () => api.get("/api/auth/me/");

// ------------------------------------------------------------
// Nutrition endpoints
// ------------------------------------------------------------
export const fetchNutritionRecommendations = (sex, age) =>
  api.get("/api/nutrition/recommendations", {
    params: { sex, age },
  });

export const fetchNutritionNutrients = () =>
  api.get("/api/nutrition/nutrients");

// ------------------------------------------------------------
// Ingredient matching (free-text -> FoodItem candidates)
// ------------------------------------------------------------
export const matchRecipeText = (text, top_n) =>
  api.post("/api/foods/match/", { text, ...(top_n ? { top_n } : {}) });

/** Convert amount+unit to grams for a specific FoodItem (after user picks a match). */
export const convertIngredientForFood = ({ food_id, amount, unit, name }) =>
  api.post("/api/foods/match/", { food_id, amount, unit, name });

export const fetchFoodById = (id) => api.get(`/api/foods/${id}/`);

// ------------------------------------------------------------
// Recipe endpoints
// ------------------------------------------------------------

// Current user's recipes (paginated). Params: page, page_size, q, filter (all|mine|saved).
export const fetchMyRecipes = (token, params) =>
  api.get("/api/recipes/", { ...authConfig(token), params });

// All public recipes. Supports optional pagination params: page, page_size, q.
export const fetchAllRecipes = (tokenOrParams, maybeParams) => {
  const token = typeof tokenOrParams === "string" ? tokenOrParams : undefined;
  const params =
    maybeParams ||
    (tokenOrParams && typeof tokenOrParams === "object"
      ? tokenOrParams
      : undefined);
  const config = authConfig(token) || {};
  return api.get("/api/recipes/all/", { ...config, params });
};

// Saved recipes for current user
export const fetchSavedRecipes = (token) =>
  api.get("/api/recipes/saved/", authConfig(token));

// Single recipe (ownership is enforced server-side)
export const fetchRecipeById = (id, token) =>
  api.get(`/api/recipes/${id}/`, authConfig(token));

// Public recipe by slug
export const fetchRecipeBySlug = (slug, token) =>
  api.get(`/api/recipes/${encodeURIComponent(slug)}/`, authConfig(token));

// Create new recipe
export const createRecipe = (payload, token) =>
  api.post("/api/recipes/", payload, authConfig(token));

// Update recipe
export const updateRecipe = (id, payload, token) =>
  api.put(`/api/recipes/${id}/`, payload, authConfig(token));

// Delete recipe
export const deleteRecipe = (id, token) =>
  api.delete(`/api/recipes/${id}/`, authConfig(token));

// Save (bookmark) recipe
export const saveRecipeForUser = (id, token) =>
  api.post(`/api/recipes/${id}/save/`, null, authConfig(token));

// Fork (create an editable private copy of) a recipe
export const forkRecipe = (id, token) =>
  api.post(`/api/recipes/${id}/fork/`, null, authConfig(token));

// Unsave recipe
export const unsaveRecipeForUser = (id, token) =>
  api.delete(`/api/recipes/${id}/save/`, authConfig(token));

// ------------------------------------------------------------
// Content endpoints
// ------------------------------------------------------------
export const fetchArticles = (paramsOrQ) => {
  const params =
    typeof paramsOrQ === "string"
      ? { q: paramsOrQ }
      : paramsOrQ && typeof paramsOrQ === "object"
      ? paramsOrQ
      : undefined;
  return api.get("/api/content/articles/", params ? { params } : undefined);
};

export const fetchArticleBySlug = (slug) =>
  api.get(`/api/content/articles/${slug}/`);

export default api;
